import * as Yup from "yup";
import { parseBrazilianNumber, verifyCnpj } from "../../utils/utilityFunctions";
import { extractMeasuringPoints } from "./bess/bessCsv";
import { FormValues, SituacaoUsina } from "./types";
import {
  EXIGE_MEDICAO,
  ocultaCampo,
  CampoUsinaOcultavel,
} from "./situacaoUsina";

// Versão do shape salvo no histórico (simulation_data). Bumpar a cada campo novo
// e tratar a migração em normalizeSimulationHistoryItem.
export const CURRENT_FORM_SCHEMA = "1.2";

export const parseInitialYearSelectValue = (value: string): number =>
  Number(value);

export const toNumber = (_value: unknown, original: unknown) =>
  parseBrazilianNumber(original);

export const toPositiveNumber = (_value: unknown, original: unknown) => {
  const parsed = parseBrazilianNumber(original);
  return parsed <= 0 ? NaN : parsed;
};

export const toNonNegativeNumber = (_value: unknown, original: unknown) => {
  const parsed = parseBrazilianNumber(original);
  return parsed < 0 ? NaN : parsed;
};

export const buildInitialValues = (fortlev?: boolean): FormValues => {
  let today = new Date();

  today = new Date(
    today.getMonth() >= 5 ? today.setFullYear(today.getFullYear() + 1) : today
  );

  return {
    schema: CURRENT_FORM_SCHEMA,
    simulationType: fortlev ? "APE" : "ML",
    calc: fortlev ? "APE" : "ML",
    distribuidora: "",
    uf: "",
    ths: "VERDE",
    grupoTarifario: "",
    demandaForaPonta: "",
    demandaForaPontaUltrapassagem: "",
    consumoForaPonta: "",
    consumoPonta: "",
    demandaPonta: "0",
    demandaPontaUltrapassagem: "0",
    desconto: -1,
    tipoContratacao: "atacado",
    precoPadrao: true,
    local: true,
    cnpj: "",
    pis: "",
    cofins: "",
    icms: "",
    geracao: "",
    demandaUsina: "",
    potenciaUsina: "",
    custoProjeto: "",
    custoGestao: "",
    nomeCliente: "",
    isTaxCovid: false,
    isTaxScarcity: true,
    isEncargos: true,
    abateICMS: true,
    situacaoUsina: "NOVA",
    descontoTusdApe: false,
    simultaneidade: 65,
    precoEnergiaGeracao: ["98", "113", "124", "138", "149"],
    precoEnergia: ["98", "113", "124", "138", "149"],
    isGerador: false,
    consumoGerador: "",
    custoDiesel: "2,00",
    custoGerador: "",
    isBateria: false,
    isDescontoGarantido: true,
    potenciaBateria: "",
    energiaMaximaArmazenada: "",
    rteBateria: "",
    investimentoBateria: "",
    medicaoCsv: "",
    medicaoCsvFileName: "",
    submercado: "",
    dod: "90",
    pontoMedicaoBess: "",
    mesReferencia: "",
    perfilConsumo: "",
    generateProposal: false,
    taxaAtratividade: "14,25",
    reajusteDistribuidora: "7",
    reajusteEnergia: "5",
    lcoe: "",
    clientLogo: null,
    partnerLogo: null,
    projectImage1: null,
    projectImage2: null,
    localization: "",
    modules: "",
    inverter: "",
    structure: "",
    items: [],
    initialYear: today.getFullYear(),
    quantidadeAnos: 5,
  };
};

// Campos ocultos não devem sofrer conversão numérica nem bloquear o envio.
const plantField = (field: CampoUsinaOcultavel, schema: Yup.AnySchema) =>
  Yup.mixed().when(["calc", "situacaoUsina"], {
    is: (calc: string, situacao: SituacaoUsina = "NOVA") =>
      calc === "GDXAPE"
        ? ["geracao", "demandaUsina", "potenciaUsina"].includes(field)
        : ["APE", "MLXAPE"].includes(calc) && !ocultaCampo(situacao, field),
    then: schema,
  });

const generatorField = (schema: Yup.AnySchema) =>
  Yup.mixed().when(["isGerador", "simulationType"], {
    is: (enabled: boolean, type: string) =>
      enabled && !["MLXAPE", "MLBESS", "APEBESS"].includes(type),
    then: schema,
  });

const batteryField = (schema: Yup.AnySchema) =>
  Yup.mixed().when("isBateria", { is: true, then: schema });

const usesProposal = (enabled: boolean, type: string) =>
  enabled && ["APE", "MLXAPE"].includes(type);

export const simulationValidationSchema = Yup.object({
  distribuidora: Yup.string().required("A distribuidora deve ser informada"),
  grupoTarifario: Yup.string().required("O grupo tarifário deve ser informado"),
  nomeCliente: Yup.string()
    .max(23, "O nome do cliente deve ter no máximo 23 caracteres")
    .test(
      "max-length",
      "O nome do cliente deve ter no máximo 23 caracteres",
      function (value) {
        if (value && value.length > 23) {
          return false; // Retorna false para indicar que a validação falhou
        }
        return true; // Retorna true para indicar que a validação passou
      }
    ),
  demandaForaPonta: Yup.number()
    .transform(toPositiveNumber)
    .typeError("Demanda fora de Ponta precisa ser um número valido")
    .required("Demanda Fora de Ponta deve ser informada"),
  demandaForaPontaUltrapassagem: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError(
      "Demanda fora de Ponta Ultrapassagem precisa ser um número valido"
    ),
  demandaPonta: Yup.number()
    .transform(toNumber)
    .typeError("Demanda de Ponta precisa ser um número valido")
    .required("Demanda de Ponta deve ser informado"),
  demandaPontaUltrapassagem: Yup.number()
    .transform(toNumber)
    .typeError("Demanda de Ponta Ultrapassagem precisa ser um número valido"),
  consumoPonta: Yup.number()
    .transform(toPositiveNumber)
    .typeError("Consumo de Ponta precisa ser um número valido")
    .required("Consumo de Ponta deve ser informado"),
  consumoForaPonta: Yup.number()
    .transform(toPositiveNumber)
    .typeError("Consumo Fora de Ponta precisa ser um número valido")
    .required("Consumo Fora de Ponta deve ser informado"),
  consumoGerador: generatorField(
    Yup.number()
      .transform(toPositiveNumber)
      .typeError("Consumo do gerador precisa ser um número valido")
  ),

  custoDiesel: generatorField(
    Yup.number()
      .transform(toNumber)
      .typeError("Custo do diesel precisa ser um número valido")
  ),

  custoGerador: generatorField(
    Yup.number()
      .transform(toNumber)
      .typeError("Custo do diesel precisa ser um número valido")
  ),

  potenciaBateria: batteryField(
    Yup.number()
      .transform(toNumber)
      .positive("Potência da bateria precisa ser um valor positivo")
      .typeError("Potência da bateria precisa ser um número valido")
      .required(
        "A potência da bateria deve ser informada na simulação com bateria"
      )
  ),

  energiaMaximaArmazenada: batteryField(
    Yup.number()
      .transform(toNumber)
      .positive("Energia máxima armazenada precisa ser um valor positivo")
      .typeError("Energia máxima armazenada precisa ser um número valido")
      .required(
        "A energia máxima armazenável deve ser informada na simulação com bateria"
      )
  ),

  investimentoBateria: batteryField(
    Yup.number()
      .transform(toNumber)
      .positive("Investimento da bateria precisa ser um valor positivo")
      .typeError("Investimento da bateria precisa ser um número valido")
  ),

  rteBateria: batteryField(
    Yup.number()
      .transform(toNumber)
      .typeError("O RTE precisa ser um número valido")
      .min(0, "O RTE deve ser no mínimo 0%")
      .max(100, "O RTE deve ser no máximo 100%")
      .required("O RTE deve ser informado na simulação com bateria")
  ),

  // Submercado e DoD valem para qualquer simulação com bateria: sem medição a
  // série é sintética, mas o PLD e a profundidade de descarga seguem iguais.
  submercado: Yup.string().when("isBateria", {
    is: true,
    then: Yup.string().required(
      "O submercado deve ser informado na simulação com bateria"
    ),
  }),
  dod: batteryField(
    Yup.number()
      .transform(toNumber)
      .typeError("O DoD precisa ser um número valido")
      .moreThan(0, "O DoD deve ser maior que 0%")
      .max(100, "O DoD deve ser no máximo 100%")
      .required("O DoD deve ser informado na simulação com bateria")
  ),
  // Modo sintético: sem medição, mês e perfil substituem a série medida.
  mesReferencia: Yup.string().when(["isBateria", "medicaoCsv"], {
    is: (isBateria?: boolean, csv?: string) => !!isBateria && !csv,
    then: Yup.string()
      .matches(/^\d{4}-\d{2}$/, "O mês de referência deve ser informado")
      .required("O mês de referência deve ser informado"),
  }),
  perfilConsumo: Yup.string().when(["isBateria", "medicaoCsv"], {
    is: (isBateria?: boolean, csv?: string) => !!isBateria && !csv,
    then: Yup.string().required("O perfil de consumo deve ser informado"),
  }),
  // Usina existente ou em expansão só simula bateria a partir da medição real.
  medicaoCsv: Yup.string().when(["isBateria", "calc", "situacaoUsina"], {
    is: (isBateria?: boolean, calc?: string, situacaoUsina?: SituacaoUsina) =>
      !!isBateria &&
      calc === "APE" &&
      !!situacaoUsina &&
      EXIGE_MEDICAO.includes(situacaoUsina),
    then: Yup.string().required(
      "O arquivo de medição deve ser enviado para usina existente ou em expansão"
    ),
  }),
  pontoMedicaoBess: Yup.string().when(["isBateria", "medicaoCsv"], {
    is: (isBateria?: boolean, csv?: string) =>
      !!isBateria && !!csv && extractMeasuringPoints(csv).length > 1,
    then: Yup.string().required(
      "O ponto de medição da bateria deve ser informado"
    ),
  }),
  pis: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("PIS precisa ser um número valido")
    .min(0, "O PIS deve ser no mínimo 0%")
    .max(100, "O PIS deve ser no máximo 100%")
    .required("PIS deve ser informado"),
  cofins: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("COFINS precisa ser um número valido")
    .min(0, "O COFINS deve ser no mínimo 0%")
    .max(100, "O COFINS deve ser no máximo 100%")
    .required("COFINS deve ser informado"),
  icms: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("ICMS precisa ser um número valido")
    .min(0, "O ICMS deve ser no mínimo 0%")
    .max(100, "O ICMS deve ser no máximo 100%")
    .required("ICMS deve ser informado"),
  cnpj: Yup.string()
    .required("É necessário informar o CNPJ da Empresa")
    .test("is-cnpj", "CNPJ não é valido", (value) => verifyCnpj(value ?? "")),
  custoGestao: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("O custo operacional e CCEE deve ser um número positivo"),
  precoEnergia: Yup.array()
    .of(
      Yup.number()
        .transform(toNonNegativeNumber)
        .typeError("O preço da energia deve ser um número")
        .positive("O preço da energia precisa ser um valor positivo")
        .required("O preço da energia deve ser informado")
    )
    .min(1, "Pelo menos um preço de energia deve ser informado")
    .required("Os preços de energia devem ser informados"),
  desconto: Yup.number()
    .moreThan(-1, "O tipo de desconto deve ser informado")
    .required("O tipo de desconto deve ser informado"),
  tipoContratacao: Yup.string().required(
    "O tipo de contratação deve ser informado"
  ),
  precoPadrao: Yup.string().required("O preço padrão deve ser informado"),
  taxaAtratividade: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("Este campo precisa ser um número valido")
    .min(0, "A taxa de atratividade deve ser no mínimo 0%")
    .max(100, "A taxa de atratividade deve ser no máximo 100%")
    .required("Campo obrigatório"),
  reajusteDistribuidora: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("Este campo precisa ser um número valido")
    .min(0, "O reajuste da distribuidora deve ser no mínimo 0%")
    .max(100, "O reajuste da distribuidora deve ser no máximo 100%")
    .required("Campo obrigatório"),
  reajusteEnergia: Yup.number()
    .transform(toNonNegativeNumber)
    .typeError("Este campo precisa ser um número valido")
    .min(0, "O reajuste da energia deve ser no mínimo 0%")
    .max(100, "O reajuste da energia deve ser no máximo 100%")
    .required("Campo obrigatório"),
  lcoe: Yup.mixed().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.number()
      .transform(toNonNegativeNumber)
      .typeError("Este campo precisa ser um número valido")
      .required("Campo obrigatório"),
  }),
  localization: Yup.string().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.string().required("Campo obrigatório"),
  }),
  modules: Yup.string().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.string().required("Campo obrigatório"),
  }),
  inverter: Yup.string().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.string().required("Campo obrigatório"),
  }),
  structure: Yup.string().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.string().required("Campo obrigatório"),
  }),
  items: Yup.array().when(["generateProposal", "simulationType"], {
    is: usesProposal,
    then: Yup.array()
      .min(1)
      .required("Campo obrigatório")
      .of(
        Yup.object({
          nome: Yup.string().required("Nome é obrigatório"),
          potencia: Yup.number()
            .transform(toPositiveNumber)
            .typeError("Este campo precisa ser um número valido")
            .required("Este campo deve ser informado"),
          geradorFotovoltaico: Yup.number()
            .transform(toPositiveNumber)
            .typeError("Este campo precisa ser um número valido")
            .required("Este campo deve ser informado"),
          projetoHomologacao: Yup.number()
            .transform(toPositiveNumber)
            .typeError("Este campo precisa ser um número valido")
            .required("Este campo deve ser informado"),
          epc: Yup.number()
            .transform(toPositiveNumber)
            .typeError("Este campo precisa ser um número valido")
            .required("Este campo deve ser informado"),
        })
      ),
  }),
  custoProjeto: plantField(
    "custoProjeto",
    Yup.number()
      .transform(toNonNegativeNumber)
      .typeError("O valor de investimento deve ser um número positivo")
      .required("O valor de investimento deve ser informado")
  ),
  demandaUsina: plantField(
    "demandaUsina",
    Yup.number()
      .transform(toNonNegativeNumber)
      .typeError("A demanda da usina deve ser um número")
      .required("A demanda da usina deve ser informada")
  ),
  potenciaUsina: plantField(
    "potenciaUsina",
    Yup.number()
      .transform(toNonNegativeNumber)
      .typeError("A potência da usina deve ser um número")
      .required("A potência da usina deve ser informada")
  ),
  geracao: plantField(
    "geracao",
    Yup.number()
      .transform(toNonNegativeNumber)
      .typeError("A geração da usina deve ser um número")
      .required("A geração da usina deve ser informada")
  ),
  simultaneidade: plantField(
    "simultaneidade",
    Yup.number()
      .typeError("A simultaneidade deve ser um número")
      .moreThan(-1, "A simultaneidade deve ser um valor de porcentagem válido")
      .lessThan(101, "A simultaneidade deve ser um valor de porcentagem válido")
      .required("A simultaneidade deve ser informada")
  ),
});
